import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import { 
  trackATSScoreAnalysisStarted, 
  trackATSScoreAnalysisSuccess, 
  trackATSScoreAnalysisError 
} from '../utils/analytics';

// Initialize the worker using unpkg CDN for reliable production builds
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface ATSResult {
  score: number;
  strengths: string[];
  improvements: string[];
  summary: string;
  analyzed_with_job_description: boolean;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const ATSScorePage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [jobDescription, setJobDescription] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ATSResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') {
      return 'Please upload a PDF file only.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 2MB.';
    }
    return null;
  };

  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n';
    }

    return fullText.trim();
  };

  const handleFile = useCallback((selectedFile: File) => {
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setFile(selectedFile);
    setResult(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  }, [handleFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  }, [handleFile]);

  const handleRemoveFile = () => {
    setFile(null);
    setError(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!file) return;

    const hasJobDescription = Boolean(jobDescription.trim());
    
    setIsLoading(true);
    setError(null);

    // Track analysis started
    trackATSScoreAnalysisStarted(hasJobDescription, file.size);

    try {
      // Extract text from PDF
      let resumeText: string;
      try {
        resumeText = await extractTextFromPDF(file);
      } catch (pdfError) {
        const errorMsg = 'Failed to read PDF. The file may be corrupted or password-protected.';
        trackATSScoreAnalysisError('pdf_extraction_error', errorMsg, hasJobDescription);
        throw new Error(errorMsg);
      }
      
      if (!resumeText.trim()) {
        const errorMsg = 'Could not extract text from PDF. Please ensure the PDF contains selectable text (not a scanned image).';
        trackATSScoreAnalysisError('empty_pdf_text', errorMsg, hasJobDescription);
        throw new Error(errorMsg);
      }

      // Check for API base URL
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiBaseUrl) {
        const errorMsg = 'API configuration error. Please contact support.';
        trackATSScoreAnalysisError('api_config_error', errorMsg, hasJobDescription);
        throw new Error(errorMsg);
      }

      // Make API request
      let response: Response;
      try {
        response = await fetch(`${apiBaseUrl}/api/public/ats-score`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            resume_text: resumeText,
            job_description: jobDescription.trim() || undefined,
          }),
        });
      } catch (networkError) {
        const errorMsg = 'Network error. Please check your internet connection and try again.';
        trackATSScoreAnalysisError('network_error', errorMsg, hasJobDescription);
        throw new Error(errorMsg);
      }

      // Handle HTTP errors
      if (!response.ok) {
        let errorMsg: string;
        let errorType: string;
        
        if (response.status === 429) {
          errorType = 'rate_limit';
          errorMsg = 'Too many requests. Please wait a moment and try again.';
        } else if (response.status === 413) {
          errorType = 'payload_too_large';
          errorMsg = 'Resume content is too large. Please try a shorter resume.';
        } else if (response.status >= 500) {
          errorType = 'server_error';
          errorMsg = 'Server error. Please try again later.';
        } else {
          errorType = `http_${response.status}`;
          errorMsg = 'Failed to analyze resume. Please try again.';
        }
        
        trackATSScoreAnalysisError(errorType, errorMsg, hasJobDescription);
        throw new Error(errorMsg);
      }

      // Parse response
      let data: ATSResult;
      try {
        data = await response.json();
      } catch (parseError) {
        const errorMsg = 'Invalid response from server. Please try again.';
        trackATSScoreAnalysisError('json_parse_error', errorMsg, hasJobDescription);
        throw new Error(errorMsg);
      }

      // Track success
      trackATSScoreAnalysisSuccess(
        data.score, 
        data.analyzed_with_job_description, 
        data.strengths.length, 
        data.improvements.length
      );

      setResult(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/logo.svg" 
                alt="Vertex Find Logo" 
                className="w-8 h-8"
              />
              <span className="text-base font-semibold text-white">
                Vertex Find
              </span>
            </Link>
            <Link 
              to="/" 
              className="text-sm font-medium text-gray-300 hover:text-white transition-colors"
            >
              ← Back to Home
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12 animate-fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              ATS Resume <span className="text-primary-400">Score</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Upload your resume to get an instant ATS compatibility score with actionable feedback to improve your chances.
            </p>
          </div>

          {!result ? (
            /* Upload Form */
            <div className="space-y-8 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
              {/* File Upload Zone */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8">
                <label className="block text-sm font-medium text-gray-300 mb-4">
                  Resume (PDF only, max 2MB)
                </label>
                
                {!file ? (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                      relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer
                      transition-all duration-200
                      ${isDragging 
                        ? 'border-primary-500 bg-primary-500/10' 
                        : 'border-gray-700 hover:border-gray-600 hover:bg-gray-800/50'
                      }
                    `}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handleFileInput}
                      className="hidden"
                    />
                    
                    <div className="flex flex-col items-center">
                      <svg 
                        className={`w-12 h-12 mb-4 ${isDragging ? 'text-primary-400' : 'text-gray-500'}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={1.5} 
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                        />
                      </svg>
                      <p className="text-white font-medium mb-1">
                        {isDragging ? 'Drop your resume here' : 'Drag & drop your resume'}
                      </p>
                      <p className="text-gray-500 text-sm">
                        or click to browse
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-gray-800 rounded-xl p-4 border border-gray-700">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-medium">{file.name}</p>
                        <p className="text-gray-500 text-sm">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveFile}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Job Description */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-300">
                    Job Description <span className="text-gray-500">(optional)</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    {jobDescription.length} characters
                  </span>
                </div>
                <textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the job description here for a more tailored analysis..."
                  rows={6}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-center space-x-3">
                  <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={!file || isLoading}
                className={`
                  w-full py-4 rounded-full font-medium text-lg transition-all duration-200
                  ${file && !isLoading
                    ? 'bg-white text-black hover:bg-gray-200'
                    : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center space-x-2">
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Analyzing Resume...</span>
                  </span>
                ) : (
                  'Analyze Resume'
                )}
              </button>
            </div>
          ) : (
            /* Results */
            <div className="space-y-8 animate-fadeInUp">
              {/* Score Card */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8 text-center">
                <div className="relative inline-flex items-center justify-center mb-6">
                  <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-gray-800"
                    />
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="url(#scoreGradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${(result.score / 100) * 440} 440`}
                      className="transition-all duration-1000 ease-out"
                    />
                    <defs>
                      <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" className={`${result.score >= 80 ? 'stop-green-500' : result.score >= 60 ? 'stop-yellow-500' : 'stop-red-500'}`} stopColor={result.score >= 80 ? '#22c55e' : result.score >= 60 ? '#eab308' : '#ef4444'} />
                        <stop offset="100%" className={`${result.score >= 80 ? 'stop-green-400' : result.score >= 60 ? 'stop-yellow-400' : 'stop-red-400'}`} stopColor={result.score >= 80 ? '#4ade80' : result.score >= 60 ? '#facc15' : '#f87171'} />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-5xl font-bold ${getScoreColor(result.score)}`}>
                      {result.score}
                    </span>
                    <span className="text-gray-500 text-sm">out of 100</span>
                  </div>
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-2">ATS Compatibility Score</h2>
                
                {result.analyzed_with_job_description && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-500/20 text-primary-400 text-sm">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Analyzed with job description
                  </span>
                )}
              </div>

              {/* Summary */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8">
                <h3 className="text-lg font-semibold text-white mb-4">Summary</h3>
                <p className="text-gray-300 leading-relaxed">{result.summary}</p>
              </div>

              {/* Strengths & Improvements Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Strengths */}
                <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8">
                  <div className="flex items-center space-x-2 mb-6">
                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white">Strengths</h3>
                  </div>
                  <ul className="space-y-3">
                    {result.strengths.map((strength, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                        </svg>
                        <span className="text-gray-300">{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Improvements */}
                <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8">
                  <div className="flex items-center space-x-2 mb-6">
                    <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white">Areas to Improve</h3>
                  </div>
                  <ul className="space-y-3">
                    {result.improvements.map((improvement, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <svg className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span className="text-gray-300">{improvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Analyze Another Button */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    setResult(null);
                    setFile(null);
                    setJobDescription('');
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="btn-primary px-8 py-4"
                >
                  Analyze Another Resume
                </button>
                <Link to="/" className="btn-secondary px-8 py-4 text-center">
                  Back to Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ATSScorePage;
